<script setup>
import {useAppNotifications} from '~/composables/useAppNotifications'
import {useTabletMode} from '~/composables/useTabletMode'
import {useNetworkMonitor} from '~/composables/useNetworkMonitor'
import {usePosDisplaySettings} from '~/composables/usePosDisplaySettings'

const DefaultLayoutWithTabNav = defineAsyncComponent(() => import('./components/DefaultLayoutWithTabNav.vue'))
const DefaultLayoutWithHorizontalNav = defineAsyncComponent(() => import('./components/DefaultLayoutWithHorizontalNav.vue'))

const {layoutAttrs, injectSkinClasses} = useSkins()

injectSkinClasses()

const {notificationStore} = useAppNotifications()
const router = useRouter()
const {isTabletMode, loadTabletMode} = useTabletMode()
const {isOnline, hasInternet} = useNetworkMonitor()
const {fontFamily, fontWeight, baseFontSize, compactMode, navStyle, loadSettings: loadDisplaySettings} = usePosDisplaySettings()

const isOffline = computed(() => !isOnline.value || !hasInternet.value)

const currentLayout = computed(() => {
  return navStyle.value === 'horizontal' ? DefaultLayoutWithHorizontalNav : DefaultLayoutWithTabNav
})

const posStyles = computed(() => ({
  '--pos-font-family': fontFamily.value,
  '--pos-font-weight': fontWeight.value,
  '--pos-base-font-size': `${baseFontSize.value}px`,
  '--pos-font-scale': Number(baseFontSize.value) / 14,
  fontFamily: fontFamily.value,
  fontWeight: fontWeight.value,
  fontSize: `${baseFontSize.value}px`
}))

onMounted(async () => {
  console.log('[default.vue] Loading settings...')
  await Promise.all([loadTabletMode(), loadDisplaySettings()])
  console.log('[default.vue] Nav style:', navStyle.value)
})

const handleSnackbarAction = () => {
  if (notificationStore.snackbar.link) {
    router.push(notificationStore.snackbar.link)
  }
  notificationStore.hideSnackbar()
}
</script>

<template>
  <div class="pos-app-container" :class="{ 'pos-compact': compactMode }" :style="posStyles">
    <component :is="currentLayout" v-bind="layoutAttrs">
      <slot/>
    </component>
  </div>

  <VBanner
    v-if="isOffline"
    color="warning"
    icon="mdi mdi-wifi-off"
    class="offline-banner"
    sticky
  >
    <span class="font-weight-medium">Working Offline</span> - Orders will sync when connection is restored. Cash payments only.
  </VBanner>

  <PatronectArrivalNotification/>
  <CustomerDetailsDialog/>

  <VSnackbar
    v-model="notificationStore.snackbar.show"
    :timeout="notificationStore.snackbar.timeout"
    color="success"
    location="top right"
    multi-line
  >
    <div class="d-flex align-center">
      <VIcon class="mdi mr-2">mdi-bell-ring</VIcon>
      <span>{{ notificationStore.snackbar.message }}</span>
    </div>

    <template #actions>
      <VBtn
        v-if="notificationStore.snackbar.link"
        color="white"
        variant="text"
        @click="handleSnackbarAction"
      >
        View
      </VBtn>
      <VBtn
        color="white"
        variant="text"
        @click="notificationStore.hideSnackbar"
      >
        Dismiss
      </VBtn>
    </template>
  </VSnackbar>
</template>

<style lang="scss">
// As we are using `layouts` plugin we need its styles to be imported
@use "@layouts/styles/default-layout";

.pos-app-container {
  height: 100vh;
  overflow: hidden;
}

.offline-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
}
</style>
